import { useEffect, useMemo, useState } from 'react';
import type { DateRange } from 'react-day-picker';
import { useSelector } from 'react-redux';
import DateRangePicker from '../components/DateRangePicker';
import type { RootState } from '../ApplicationState';
import { selectClassroom } from '../Classrooms';
import { getJob, startClassroomAttendanceChartJob } from '../BackendInterface';
import { getCanonicalAttendanceDateFromDate } from '../TakweenAttendance/attendanceHelpers';
import type { ClassroomAttendanceChartResponse } from '../BackendInterface';
import AttendanceTrendChart from './AttendanceTrendChart';
import { mapAttendanceChartResponseToChartData } from './attendanceReportUtils';
import styles from './AttendanceReport.module.scss';

function getCurrentSchoolYearStartDate(referenceDate: Date): Date {
  const year = referenceDate.getMonth() >= 6 ? referenceDate.getFullYear() : referenceDate.getFullYear() - 1;
  return new Date(year, 6, 1);
}

const AttendanceReport = (): JSX.Element => {
  const classroom = useSelector((state: RootState) => selectClassroom(state));
  const today = useMemo(() => new Date(), []);
  const defaultStartDate = useMemo(() => getCurrentSchoolYearStartDate(today), [today]);
  const initialDateRange = useMemo<DateRange>(() => ({ from: defaultStartDate, to: today }), [defaultStartDate, today]);

  const [dateRange, setDateRange] = useState<DateRange>(initialDateRange);
  const [isDisplayDayPicker, setIsDisplayDayPicker] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [reportData, setReportData] = useState<ClassroomAttendanceChartResponse | null>(null);

  const startDate = dateRange.from;
  const endDate = dateRange.to;

  useEffect(() => {
    let canceled = false;

    async function pollChartJob(jobId: string): Promise<ClassroomAttendanceChartResponse> {
      const startTime = Date.now();
      const timeoutMs = 2 * 60 * 1000;

      while (!canceled) {
        const job = await getJob(jobId);

        if (job.status === 'Done') {
          const payload = typeof job.data === 'string' ? JSON.parse(job.data) : job.data;
          return payload as ClassroomAttendanceChartResponse;
        }

        if (job.status === 'Error') {
          throw new Error('Attendance chart generation failed. Please try again.');
        }

        if (Date.now() - startTime > timeoutMs) {
          throw new Error('Attendance chart generation is taking longer than expected. Please try again in a moment.');
        }

        await new Promise<void>((resolve) => {
          setTimeout(() => resolve(), 1500);
        });
      }

      throw new Error('Attendance chart request was cancelled.');
    }

    async function loadReportData() {
      if (!classroom?.classroomId || !startDate || !endDate) {
        return;
      }

      setIsLoading(true);
      setErrorMessage('');

      try {
        const { jobId } = await startClassroomAttendanceChartJob(classroom.classroomId, {
          startDate: getCanonicalAttendanceDateFromDate(startDate),
          endDate: getCanonicalAttendanceDateFromDate(endDate),
          groupBy: 'lesson',
        });

        const response = await pollChartJob(jobId);

        if (!canceled) {
          setReportData(response);
        }
      } catch (error) {
        if (!canceled) {
          setReportData(null);
          setErrorMessage(
            error instanceof Error
              ? error.message
              : 'Unable to load attendance report data. Please try again in a few minutes.',
          );
        }
      } finally {
        if (!canceled) {
          setIsLoading(false);
        }
      }
    }

    void loadReportData();

    return () => {
      canceled = true;
    };
  }, [classroom?.classroomId, startDate, endDate]);

  const chartData = useMemo(() => {
    return mapAttendanceChartResponseToChartData(reportData);
  }, [reportData]);

  const hasNoData = !isLoading && !errorMessage && chartData.length === 0;

  return (
    <div className={styles.container}>
      <div className={styles.centeringContainer}>
        <div className={styles.titleAndSubtitle}>
          <h1>Attendance Reporting</h1>
          <h2>{classroom?.className || 'Current Classroom'}</h2>
        </div>
        <div className={styles.dateAndSubmit}>
          <DateRangePicker
            dateRange={dateRange}
            setDateRange={setDateRange}
            startDate={dateRange.from || defaultStartDate}
            excludeAfter={today}
            beforeFirstInputText='Start Date:'
            beforeSecondInputText='End Date:'
            isDisplayCalendar={isDisplayDayPicker}
            setIsDisplayCalendar={setIsDisplayDayPicker}
          />
        </div>
        <AttendanceTrendChart data={chartData} isLoading={isLoading} />
        {hasNoData && (
          <div className={styles.noDataMessage}>
            No attendance records were found for this classroom in the selected date range.
          </div>
        )}
        {!!errorMessage && (
          <div className={styles.errorMessage}>
            Unable to load attendance trends right now. Please try again in a few minutes.
          </div>
        )}
      </div>
    </div>
  );
};

export default AttendanceReport;
